import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { CheckCircle2, Copy, ExternalLink, Loader2, XCircle } from "lucide-react";
import { api } from "../../../lib/api";
import { copyText } from "../../../lib/clipboard";
import { inr } from "../pricing/PricingUi";

const STATUS = {
  created: ["Waiting for payment…", "text-amber-700 bg-amber-50 border-amber-200"],
  paid: ["Paid — credits added", "text-emerald-700 bg-emerald-50 border-emerald-200"],
  cancelled: ["Cancelled", "text-slate-600 bg-slate-100 border-slate-200"],
  expired: ["Expired", "text-slate-600 bg-slate-100 border-slate-200"],
};
export const linkStatus = (s) => STATUS[s] || STATUS.created;

export const LinkResult = ({ link: initial, onChange }) => {
  const [link, setLink] = useState(initial);
  useEffect(() => setLink(initial), [initial]);
  useEffect(() => {
    if (link.status !== "created") return undefined;
    const t = setInterval(async () => {
      try {
        const next = await api.adminPaymentLink(link.id);
        if (next.status !== "created") {
          setLink(next); onChange?.();
          if (next.status === "paid") toast.success(`Paid! ${next.credits} credits added to ${next.user_email}`);
        }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [link.id, link.status, onChange]);
  const [label, cls] = linkStatus(link.status);
  const sent = [link.sent_to?.phone && `SMS to ${link.sent_to.phone}`, link.sent_to?.email && `email to ${link.sent_to.email}`].filter(Boolean).join(" and ");

  return (
    <div className="space-y-4 text-center" data-testid="payment-link-result">
      <div className={`mx-auto w-fit p-3 rounded-2xl border bg-white ${link.status === "created" ? "border-slate-200" : "opacity-40 border-slate-100"}`}>
        <QRCodeSVG value={link.short_url} size={196} level="M" data-testid="payment-link-qr" />
      </div>
      <p className="text-2xl font-bold text-slate-900 font-mono" data-testid="payment-link-amount">{inr(link.amount_paise / 100)}</p>
      <p className="text-sm text-slate-500 -mt-3">{link.credits} credits for {link.user_name || link.user_email}</p>
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${cls}`} data-testid="payment-link-status">
        {link.status === "created" && <Loader2 size={12} className="animate-spin" />}
        {link.status === "paid" && <CheckCircle2 size={12} />}{["cancelled", "expired"].includes(link.status) && <XCircle size={12} />} {label}
      </span>
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
        <span className="flex-1 truncate text-left text-xs font-mono text-slate-700" data-testid="payment-link-url">{link.short_url}</span>
        <button type="button" onClick={() => copyText(link.short_url, "Link copied")} data-testid="payment-link-copy" className="text-slate-500 hover:text-sky-600" aria-label="Copy link"><Copy size={14} /></button>
        <a href={link.short_url} target="_blank" rel="noreferrer" data-testid="payment-link-open" className="text-slate-500 hover:text-sky-600" aria-label="Open link"><ExternalLink size={14} /></a>
      </div>
      {sent && link.status === "created" && <p className="text-xs text-slate-500" data-testid="payment-link-sent">Razorpay sent the link by {sent}. Valid for 24 hours — they can also scan the QR above.</p>}
    </div>
  );
};

export default LinkResult;
